import React from 'react';
import { Box, Container, IconButton, Typography, Stack } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import { useTTS } from '../../hooks/useTTS';
import PortalPageTemplate from './PortalPageTemplate';

export interface UnitPageShellProps {
  children?: React.ReactNode;
  columns?: { xs?: string; md?: string; lg?: string };
  gap?: number | string;
  py?: number | string;
  title?: string;
  showTTS?: boolean;
  ttsText?: string;
  actions?: React.ReactNode;
  headerExtra?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

// Zentralisierte Seiten-Schablone: Stellt Hintergrund, Container, Titel/TTS + optional Actions bereit.
// Alle Seiten sollen ausschließlich dieses Template nutzen, damit Änderungen hier global wirken.
const UnitPageShell: React.FC<UnitPageShellProps> = ({
  children,
  columns,
  gap = 3,
  py = { xs: 2, md: 4 },
  title,
  showTTS = true,
  ttsText,
  actions,
  headerExtra,
  maxWidth = false
}) => {
  const { darkMode } = useTheme();

  // Fallback Text für TTS: Alle reinen Textknoten ohne HTML Tags grob extrahieren
  const derivedTts = React.useMemo(() => {
    if (ttsText) return ttsText;
    if (!children) return '';
    // Sehr einfache Extraktion – kann bei Bedarf später durch gezielte Traversierung ersetzt werden.
    try {
      const raw = JSON.stringify(children);
      return raw
        .replace(/<[^>]+>/g, ' ') // Tags entfernen
        .replace(/\\n+/g, ' ') // Newlines glätten
        .replace(/\s{2,}/g, ' ') // Mehrfach Whitespaces
        .trim()
        .slice(0, 1500); // Begrenzen für Performance
    } catch {
      return '';
    }
  }, [children, ttsText]);

  const { speak, stop, speaking } = useTTS({ text: derivedTts });

  const handleTTS = () => {
    if (!showTTS) return;
    if (speaking) stop(); else if (derivedTts) speak();
  };

  return (
    React.createElement(PortalPageTemplate, { darkMode },
      React.createElement(Container, { 
        maxWidth: maxWidth, 
        disableGutters: maxWidth === false, 
        sx: { 
          py: maxWidth === false ? 0 : py, 
          px: maxWidth === false ? 0 : undefined,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden'
        } 
      },
        React.createElement(Stack, { spacing: 4, sx: { width: '100%', maxWidth: '100%', overflow: 'hidden' } },
          (title || actions || showTTS) && React.createElement(
            Box,
            { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 } },
            React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 } },
              title && React.createElement(Typography, {
                variant: 'h4',
                sx: { fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }
              }, title),
              showTTS && React.createElement(IconButton, { onClick: handleTTS, 'aria-label': speaking ? 'Sprachausgabe stoppen' : 'Sprachausgabe starten' },
                React.createElement('span', { style: { fontSize: 22 } }, speaking ? '⏹️' : '🔊')
              ),
              headerExtra
            ),
            actions && React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 } }, actions)
          ),
          React.createElement(Box, {
            sx: {
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              display: 'grid',
              gap,
              gridTemplateColumns: columns?.xs || '1fr',
              '@media (min-width:900px)': { gridTemplateColumns: columns?.md || columns?.xs || '1fr' },
              '@media (min-width:1200px)': { gridTemplateColumns: columns?.lg || columns?.md || columns?.xs || '1fr' }
            }
          }, children)
        )
      )
    )
  );
};

export default UnitPageShell;
